import React, { useState, useEffect, useRef } from 'react';
import {
  Button,
  Typography,
  Paper,
  Box,
  IconButton,
  Dialog,
  DialogContent,
  Stack,
} from '@mui/material';
import { Close, CloudUpload } from '@mui/icons-material';
import { useMutation, useQuery } from 'react-query';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
import { useHistory, useParams } from 'react-router-dom';
import { ROUTES } from 'module/navigation/enums/routes.enums';
import {
  getShiftNoteById,
  postShiftNote,
  putShiftNote,
  uploadShiftNoteAttachment,
  getShiftNoteAttachmentUrl,
  ShiftNoteAttachment,
} from 'core/API/logbook';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useDocumentContext } from 'module/documents/context';
import { resolveLogbookNoteDate } from 'module/logBook/utils/logbookValidation';

const MAX_IMAGES = 5;
const MAX_IMAGE_SIZE_MB = 5;

const AddShiftNotes = () => {
  const { showToast } = useToastContext();
  const { noteId } = useParams<{ noteId?: string }>();
  const history = useHistory();
  const workplaceId = parseInt(localStorage.getItem('workplace-id') || '1');
  const { startDate } = useDocumentContext();

  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [authorName, setAuthorName] = useState('');
  const [attachments, setAttachments] = useState<ShiftNoteAttachment[]>([]);
  const [attachmentsError, setAttachmentsError] = useState<string | null>(null);
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [previewImage, setPreviewImage] = useState<{ url: string; alt: string } | null>(null);

  const imageInputRef = useRef<HTMLInputElement | null>(null);

  const { data: shiftNoteById, isLoading: isNotesLoading, isError: isNoteFetchError } = useQuery(
    ['shiftNoteById', noteId],
    () => getShiftNoteById(parseInt(noteId as string, 10)),
    {
      enabled: !!noteId,
      staleTime: 0,
      refetchOnMount: true,
    }
  );

  useEffect(() => {
    if (!noteId) return;
    if (isNotesLoading) return;
    if (isNoteFetchError || !shiftNoteById) {
      setError('Note not found for editing');
      return;
    }

    const noteContent = shiftNoteById.note || '';
    setTimeout(() => {
      setNote(noteContent);
    }, 100);

    if (shiftNoteById.author && typeof shiftNoteById.author === 'object') {
      const firstName = shiftNoteById.author.firstName || '';
      const lastName = shiftNoteById.author.lastName || '';
      const fullName = `${firstName} ${lastName}`.trim();
      setAuthorName(fullName || 'Unknown User');
    } else {
      setAuthorName('Unknown User');
    }

    if (shiftNoteById.attachments && Array.isArray(shiftNoteById.attachments)) {
      const normalizedAttachments = shiftNoteById.attachments.map((att: any, index: number) => {
        if (typeof att === 'string') {
          return {
            id: index,
            '@id': att,
            fileName: `Attachment ${index + 1}`,
          } as ShiftNoteAttachment;
        }
        return att as ShiftNoteAttachment;
      });
      setAttachments(normalizedAttachments);
    }
  }, [noteId, shiftNoteById, isNotesLoading, isNoteFetchError]);

  const { mutate: createNote, isLoading: isCreating } = useMutation(postShiftNote, {
    onSuccess: () => {
      showToast('success', 'Shift Note added!');
      history.push(ROUTES.LOGBOOK);
    },
    onError: () => {
      showToast('error', 'Failed to add Shift Note.');
    },
  });

  const { mutate: updateNote, isLoading: isUpdating } = useMutation(putShiftNote, {
    onSuccess: () => {
      showToast('success', 'Shift Note updated!');
      history.push(ROUTES.LOGBOOK);
    },
    onError: () => {
      showToast('error', 'Failed to update Shift Note.');
    },
  });

  const handleRemoveAttachment = (index: number) => {
    setAttachments((prev) => {
      const next = [...prev];
      const [removed] = next.splice(index, 1);

      if (removed?.previewUrl) {
        URL.revokeObjectURL(removed.previewUrl);
      }

      return next;
    });
    setAttachmentsError(null);
  };

  const processImageFiles = async (fileList: FileList | File[]) => {
    const files = Array.from(fileList);
    if (files.length === 0) return;

    if (isUploadingImages) return;

    const errors: string[] = [];
    const remainingSlots = MAX_IMAGES - attachments.length;

    if (remainingSlots <= 0) {
      setAttachmentsError(`You can attach up to ${MAX_IMAGES} images.`);
      return;
    }

    const selectedFiles = files.slice(0, remainingSlots);
    const validFiles: File[] = [];

    selectedFiles.forEach((file) => {
      if (!file.type.startsWith('image/')) {
        errors.push('Only image files are allowed.');
        return;
      }

      if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
        const msg = `${file.name} is larger than ${MAX_IMAGE_SIZE_MB}MB.`;
        errors.push(msg);
        showToast('error', msg);
        return;
      }

      validFiles.push(file);
    });

    if (validFiles.length === 0) {
      setAttachmentsError(errors.join(' '));
      return;
    }

    try {
      setIsUploadingImages(true);
      const uploaded = await Promise.all(
        validFiles.map(async (file) => {
          const apiFile = await uploadShiftNoteAttachment(file);
          const previewUrl = URL.createObjectURL(file);

          return {
            ...apiFile,
            fileName: apiFile.fileName || file.name,
            previewUrl,
          } as ShiftNoteAttachment;
        })
      );

      setAttachments((prev) => [...prev, ...uploaded]);
      setAttachmentsError(errors.length ? errors.join(' ') : null);
    } catch (err) {
      console.error('Image upload failed:', err);
      setAttachmentsError('Failed to upload images. Please try again.');
    } finally {
      setIsUploadingImages(false);
    }
  };

  const handleImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    await processImageFiles(files);
    event.target.value = '';
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (attachments.length >= MAX_IMAGES || isUploadingImages) return;
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    if (attachments.length >= MAX_IMAGES || isUploadingImages) return;
    const files = event.dataTransfer.files;
    if (files?.length) await processImageFiles(files);
  };

  const isPickerDisabled = isUploadingImages || attachments.length >= MAX_IMAGES;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (isUploadingImages) {
      showToast('error', 'Please wait for images to finish uploading before saving.');
      return;
    }

    const hasNoteContent = !!note.trim();
    const hasAttachments = attachments.length > 0;

    if (!hasNoteContent && !hasAttachments) {
      setError('Shift note cannot be empty.');
      return;
    }
    setError('');

    const noteDate = resolveLogbookNoteDate(startDate);

    const attachmentIris = attachments.map(
      (a) => a['@id'] ?? `/api/shift_note_attachments/${a.id}`
    );

    const payload = {
      note,
      workplaceId,
      date: noteDate,
      attachments: attachmentIris,
    };

    if (noteId) {
      updateNote({ ...payload, id: parseInt(noteId) });
    } else {
      createNote(payload);
    }
  };

  if (noteId && isNotesLoading) {
    return (
      <Box sx={{ backgroundColor: '#f4f6fa', minHeight: '100vh', py: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Typography variant="h6">Loading note for editing...</Typography>
      </Box>
    );
  }

  if (noteId && error === 'Note not found for editing') {
    return (
      <Box sx={{ backgroundColor: '#f4f6fa', minHeight: '100vh', py: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Box textAlign="center">
          <Typography variant="h6" color="error" mb={2}>Note not found</Typography>
          <Button
            onClick={() => history.push(ROUTES.LOGBOOK)}
            variant="contained"
            sx={{ backgroundColor: 'rgb(68, 99, 136)' }}
          >
            Back to Logbook
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#f4f6fa', minHeight: '100vh', py: 3 }}>
      <Box maxWidth="1100px" mx="auto">
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Button
            onClick={() => history.goBack()}
            sx={{
              backgroundColor: 'rgb(68, 99, 136)',
              color: '#fff',
              textTransform: 'none',
              fontWeight: 500,
              px: 3,
              py: 0.75,
              '&:hover': { backgroundColor: 'rgb(57, 80, 111)' },
            }}
          >
            Back
          </Button>

          <Button
            type="submit"
            variant="contained"
            form="shiftNoteForm"
            sx={{
              backgroundColor: 'rgb(68, 99, 136)',
              color: '#fff',
              px: 4,
              textTransform: 'none',
              '&:hover': { backgroundColor: 'rgb(57, 80, 111)' },
            }}
            disabled={isCreating || isUpdating || isUploadingImages}
          >
            {isCreating || isUpdating ? 'Saving…' : 'Save'}
          </Button>
        </Box>

        <Paper elevation={1}>
          <Box
            sx={{
              p: 2,
              bgcolor: 'grey.100',
              borderBottom: 1,
              borderColor: 'divider',
              borderTopLeftRadius: 4,
              borderTopRightRadius: 4,
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h5" fontWeight={600}>
                {noteId ? 'Edit Shift Note' : 'Add Shift Note'}
              </Typography>
            </Box>
          </Box>
          {noteId && (
            <Box sx={{ px: 3, pt: 1.5, pb: 0.1 }}>
              <Typography variant="subtitle2">
                <strong>{authorName}</strong> — Editing existing note
              </Typography>
            </Box>
          )}
          <Box sx={{ p: 3, minHeight: 350 }}>
            <form id="shiftNoteForm" onSubmit={handleSubmit}>
              <Box
                sx={{
                  mb: 0,
                  '& .quill': {
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: 'white',
                  },
                  '& .ql-toolbar.ql-snow': {
                    flex: '0 0 auto',
                    borderColor: '#ccc',
                  },
                  '& .ql-container.ql-snow': {
                    flex: '0 0 auto',
                    height: '150px',
                    minHeight: '150px',
                    maxHeight: '150px',
                    overflowY: 'auto',
                    borderColor: '#ccc',
                  },
                  '& .ql-editor': {
                    minHeight: '150px',
                  },
                }}
              >
                <ReactQuill
                  key={noteId || 'new'}
                  value={note}
                  onChange={setNote}
                  placeholder="Enter shift note here"
                  modules={{
                    toolbar: [['bold'], [{ list: 'bullet' }]],
                    clipboard: {
                      matchVisual: false,
                    },
                  }}
                  formats={['bold', 'list', 'bullet', 'indent', 'align', 'script', 'header']}
                  preserveWhitespace={true}
                />
                {error && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {error}
                  </Typography>
                )}
              </Box>

              <Stack spacing={2} sx={{ pt: 2, mt: 2, borderTop: '1px solid #e0e0e0' }}>
                <Typography sx={{ color: '#666', fontSize: '0.875rem' }}>
                  {attachments.length} of {MAX_IMAGES} images attached
                </Typography>
                <Box
                  role="button"
                  tabIndex={isPickerDisabled ? -1 : 0}
                  aria-label="Upload image files — click or drag and drop"
                  aria-disabled={isPickerDisabled}
                  sx={{
                    border: isDragging
                      ? '2px dashed #446388'
                      : attachments.length > 0
                        ? '2px dashed #4caf50'
                        : '2px dashed #ccc',
                    borderRadius: '8px',
                    p: 3,
                    textAlign: 'center',
                    cursor: isPickerDisabled ? 'not-allowed' : 'pointer',
                    backgroundColor: isDragging ? '#eef2f7' : '#fafafa',
                    opacity: isPickerDisabled ? 0.6 : 1,
                    transition: 'border-color 0.2s, background-color 0.2s',
                  }}
                  onClick={() => {
                    if (!isPickerDisabled) imageInputRef.current?.click();
                  }}
                  onKeyDown={(e) => {
                    if (!isPickerDisabled && (e.key === 'Enter' || e.key === ' ')) {
                      imageInputRef.current?.click();
                    }
                  }}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    ref={imageInputRef}
                    style={{ display: 'none' }}
                    onChange={handleImageChange}
                    aria-label="Select image files"
                  />
                  <CloudUpload sx={{ fontSize: 48, color: '#999', mb: 1 }} />
                  <Box sx={{ mb: 0.5 }}>
                    <Typography variant="body2" component="span">
                      {attachments.length >= MAX_IMAGES
                        ? `Maximum of ${MAX_IMAGES} images reached`
                        : attachments.length > 0
                          ? 'Click or drag & drop to add more images'
                          : 'Click or drag & drop image files here'}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.75rem', color: '#999' }}>
                    Maximum file size: {MAX_IMAGE_SIZE_MB}MB | Formats: Images only | Up to {MAX_IMAGES} images
                  </Typography>

                  {attachments.length > 0 && (
                    <Box
                      display="flex"
                      flexWrap="wrap"
                      gap={2}
                      justifyContent="center"
                      sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {attachments.map((file, index) => {
                        const imageUrl =
                          file.previewUrl ||
                          getShiftNoteAttachmentUrl(file.fileName);
                        return (
                          <Box
                            key={`${file.id}-${index}`}
                            sx={{
                              position: 'relative',
                              width: 100,
                              height: 100,
                              flexShrink: 0,
                              borderRadius: 1,
                              overflow: 'hidden',
                              border: '1px solid #e0e0e0',
                              bgcolor: '#fff',
                            }}
                          >
                            {imageUrl ? (
                              <Box
                                component="img"
                                src={imageUrl}
                                alt={file.fileName || `Image ${index + 1}`}
                                onClick={() =>
                                  setPreviewImage({
                                    url: imageUrl,
                                    alt: file.fileName || `Image ${index + 1}`,
                                  })
                                }
                                sx={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  display: 'block',
                                  cursor: 'pointer',
                                }}
                              />
                            ) : (
                              <Box
                                sx={{
                                  width: '100%',
                                  height: '100%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  px: 0.5,
                                }}
                              >
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  {file.fileName || `Image ${index + 1}`}
                                </Typography>
                              </Box>
                            )}
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveAttachment(index);
                              }}
                              sx={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                bgcolor: 'rgba(0,0,0,0.5)',
                                color: 'white',
                                '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                                padding: 0.5,
                              }}
                              aria-label="Remove image"
                            >
                              <Close sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
                {isUploadingImages && (
                  <Typography variant="caption" sx={{ display: 'block' }}>
                    Uploading images...
                  </Typography>
                )}
                {attachmentsError && (
                  <Typography variant="caption" color="error">
                    {attachmentsError}
                  </Typography>
                )}
              </Stack>
            </form>
          </Box>
        </Paper>
      </Box>

      <Dialog
        open={!!previewImage}
        onClose={() => setPreviewImage(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          <IconButton
            onClick={() => setPreviewImage(null)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'rgba(0,0,0,0.5)',
              color: 'white',
              zIndex: 1,
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
            }}
            aria-label="Close preview"
          >
            <Close />
          </IconButton>
          {previewImage && (
            <Box
              component="img"
              src={previewImage.url}
              alt={previewImage.alt}
              sx={{ width: '100%', height: 'auto', display: 'block' }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AddShiftNotes;
